import {
  Component,
  TemplateRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  FormControl,
  ReactiveFormsModule,
  UntypedFormGroup,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ChooseCompletionCompletionComponent } from './choose-completion.component';
import { ChooseShowIfCompletionComponent } from './choose-showif.component';
import { NgTemplateOutlet } from '@angular/common';
import { KeyFilterModule } from 'primeng/keyfilter';
import { RenderRulesComponent } from '../../common/render-rules/render-rules.component';
import { RulesService } from '../../services/builder/rules.service';
import { TabsModule } from 'primeng/tabs';
import { ButtonModule } from 'primeng/button';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'lib-base-cpanel',
  styleUrl: './baseCustomPanelStructure.scss',
  imports: [
    RenderRulesComponent,
    ReactiveFormsModule,
    NgTemplateOutlet,
    InputTextModule,
    MatIconModule,
    ButtonModule,
    MatButtonModule,
    DialogModule,
    ChooseShowIfCompletionComponent,
    ChooseCompletionCompletionComponent,
    TabsModule,
    KeyFilterModule,
  ],
  template: `
    @switch (mode) {
      @case ('config') {
        <p-dialog
          [draggable]="false"
          [closeOnEscape]="true"
          [position]="position"
          [closable]="closable()"
          [modal]="modal()"
          [header]="header()"
          [visible]="true"
          (visibleChange)="closeConfig($event)"
          [appendTo]="'body'"
          [styleClass]="'flex flex-col md:min-w-180 lg:w-256'"
          [contentStyleClass]="'flex min-h-0 grow flex-col overflow-hidden'"
        >
          <div class="flex w-full min-h-0 grow flex-col gap-2 overflow-hidden">
            <p-tabs [value]="0" class="flex min-h-0 grow flex-col">
              <p-tablist class="shrink-0">
                <p-tab [value]="0">Impostazioni base</p-tab>
                <p-tab [value]="1">Impostazioni avanzate</p-tab>
              </p-tablist>
              <p-tabpanels class="min-h-0 grow overflow-y-auto">
                <p-tabpanel [value]="0">
                  <div class="flex flex-col">
                    <ng-template [ngTemplateOutlet]="content()"></ng-template>
                  </div>
                </p-tabpanel>
                <p-tabpanel [value]="1" class="overflow-hidden">
                  <div [formGroup]="form()" class="flex h-72 flex-col">
                    <div class="flex w-full flex-col gap-2">
                      <div class="flex flex-col">
                        <label for="id">Scegli ID</label>
                        <input
                          id="id"
                          type="text"
                          pInputText
                          formControlName="id"
                          pKeyFilter="alphanum"
                        />
                        @if (form().controls['id']?.hasError('forbiddenId')) {
                          <div class="text-red-500">
                            Hai già usato questo alias
                          </div>
                        }
                        @if (form().controls['id']?.hasError('required')) {
                          <div class="text-red-500">
                            Il campo è obbligatorio
                          </div>
                        }
                      </div>
                      <lib-render-rules
                        [hideAutocompletion]="hideAutocompletion()"
                        [form]="form()"
                        [showPickButton]="true"
                        (openedCompletion)="openAutocompletionSelection()"
                        (openedShowIf)="openShowIfSelection()"
                      />
                    </div>
                  </div>
                </p-tabpanel>
              </p-tabpanels>
            </p-tabs>
          </div>

          <ng-template pTemplate="footer">
            <div class="flex w-auto flex-row gap-2 self-end">
              <p-button
                (click)="closed.emit(false)"
                [fluid]="true"
                text
                class="w-30"
              >
                Annulla
              </p-button>
              <p-button
                (click)="closed.emit(true)"
                [fluid]="true"
                class="w-30"
                cdkFocusInitial
                [disabled]="
                  this.form().controls['id'].invalid || confirmDisabled()
                "
              >
                Conferma
              </p-button>
            </div>
          </ng-template>
        </p-dialog>
      }
      @case ('choose_showIf') {
        <lib-choose-showif-completion-component
          [codeMirrorControl]="showIfRule"
          (hide)="closeSelector()"
        />
      }
      @case ('choose_completion') {
        @if (autoCompletionRule) {
          <lib-choose-autocompletion-completion-component
            [codeMirrorControl]="autoCompletionRule"
            (hide)="closeSelector()"
          />
        }
      }
    }
  `,
})
export class baseCustomPanelStructureComponent {
  private readonly rulesService = inject(RulesService);

  readonly form = input.required<UntypedFormGroup>(); //fondamentale che abbia almeno un campo "id"
  readonly content = input.required<TemplateRef<unknown>>();
  readonly closeFn = input<(args: unknown) => unknown>((args: unknown) => args);
  readonly header = input('');
  readonly hideAutocompletion = input(false);
  /** Lets a panel hold "Conferma" back while its own content is invalid. */
  readonly confirmDisabled = input(false);

  protected modal = signal(true);
  protected closable = signal(false);

  get showIfRule() {
    return this.form().controls['showIfRule'] as FormControl<string>;
  }
  get autoCompletionRule() {
    return this.form().controls['autoCompletionRule'] as
      | FormControl<string>
      | undefined;
  }

  protected closed = output<boolean>();

  readonly filterKey = new RegExp('/^[a-zA-Z0-9_]*$/', 'gmi');

  protected mode: 'config' | 'choose_showIf' | 'choose_completion' = 'config';
  protected position:
    | 'center'
    | 'left'
    | 'top'
    | 'bottom'
    | 'right'
    | 'topleft'
    | 'topright'
    | 'bottomleft'
    | 'bottomright' = 'center';

  openAutocompletionSelection() {
    this.rulesService.selectionActive.set(true);
    this.modal.set(false);
    this.closable.set(false);
    this.position = 'top';
    this.mode = 'choose_completion';
  }

  openShowIfSelection() {
    this.rulesService.selectionActive.set(true);
    this.modal.set(false);
    this.closable.set(false);
    this.position = 'top';
    this.mode = 'choose_showIf';
  }

  closeConfig(event: boolean) {
    if (!event) this.closed.emit(false);
  }

  closeSelector() {
    this.rulesService.selectionActive.set(false);
    this.modal.set(true);
    this.closable.set(true);
    this.position = 'center';
    this.mode = 'config';
  }
}
